import { Box, Button, Typography, useMediaQuery } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { navigate } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import GoldenButton from '../../Common/Buttons/GoldenButton'
import Html from '../../Html/Html'

// Styled Components
const Container = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3.75),
}))

const GridContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '2fr 1fr',
  },
}))

const TextSection = styled(Box)(({ theme }) => ({
  order: 2,
  [theme.breakpoints.up('sm')]: {
    order: 1,
  },
}))

const ImageSection = styled(Box)(({ theme }) => ({
  order: 1,
  [theme.breakpoints.up('sm')]: {
    order: 2,
    padding: theme.spacing(0, 2.5),
  },
}))

const Title = styled(Typography)(({ theme }) => ({
  marginTop: '-10px',
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(3.75),
  },
}))

const HtmlContainer = styled('div')(({ theme }) => ({
  display: '-webkit-box',
  WebkitLineClamp: 16, // 1 for title, 15 for paragraph
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}))

const ReadMoreButton = styled(Button)(({ theme }) => ({
  textDecoration: 'underline',
  backgroundColor: 'transparent',
  color: theme.palette.secondary.main,
  display: 'flex',
  justifyContent: 'center',
  margin: theme.spacing(1.25, 'auto', 3.75),
}))

const Image = styled(GatsbyImage)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
}))

function ImageTextColumns({ data }) {
  const [reveal, setReveal] = useState(false)
  const [overflow, setOverflow] = useState(false)
  const htmlRef = useRef(null)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    const element = htmlRef.current
    if (element && element.scrollHeight > element.clientHeight) {
      setOverflow(true)
    }
  }, [])

  const revealStyle = useMemo(() => {
    if (overflow && reveal) {
      return {
        WebkitLineClamp: 'unset',
        overflow: 'visible',
      }
    }
    return null
  }, [overflow, reveal])

  const ctaBoxStyles = useMemo(
    () => ({
      display: isMobile ? 'flex' : 'block',
      justifyContent: 'center',
    }),
    [isMobile]
  )

  const handleToggleReveal = useCallback(() => {
    setReveal((prev) => !prev)
  }, [])

  const handleCtaClick = useCallback(
    (href) => () => {
      navigate(href)
    },
    []
  )

  const gridContainerStyles = useMemo(
    () => ({
      display: 'grid',
      gridTemplateColumns: isMobile
        ? '1fr'
        : data.image_position === 'right'
          ? '2fr 1fr'
          : '1fr 2fr',
      gap: theme.spacing(3),
    }),
    [isMobile, data.image_position, theme]
  )

  const textSectionStyles = useMemo(
    () => ({
      order: isMobile ? 1 : data.image_position === 'right' ? 1 : 2,
    }),
    [isMobile, data.image_position]
  )

  const imageSectionStyles = useMemo(
    () => ({
      order: isMobile ? 2 : data.image_position === 'right' ? 2 : 1,
    }),
    [isMobile, data.image_position]
  )

  return (
    <Container>
      <Box sx={gridContainerStyles}>
        <TextSection sx={textSectionStyles}>
          <Title variant="h1" color="textPrimary">
            {data.title}
          </Title>
          <HtmlContainer ref={htmlRef} style={revealStyle || undefined}>
            <Html content={data.html} />
          </HtmlContainer>

          {overflow && (
            <ReadMoreButton onClick={handleToggleReveal}>
              <Typography variant="body2">
                {reveal ? 'Read Less' : 'Read More'}
              </Typography>
            </ReadMoreButton>
          )}

          {data.cta?.title && data.cta?.href && (
            <Box sx={ctaBoxStyles}>
              <GoldenButton
                label={data.cta.title}
                handleClick={handleCtaClick(data.cta.href)}
              />
            </Box>
          )}
        </TextSection>

        <ImageSection sx={imageSectionStyles}>
          <Image
            image={getImage(data?.image?.localAsset)}
            alt={data.description || ''}
          />
        </ImageSection>
      </Box>
    </Container>
  )
}

export default ImageTextColumns